import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ApiError } from '@/lib/api';
import { decideUpdateRequest } from '../../../api/eqmsApi';
import { openControlledAuthoringDraft } from '../../../api/authoringApi';
import { EQMS_QUERY_KEYS } from '../../../hooks/useEqmsDocuments';
import type { EqmsUpdateRequest, EqmsUpdateRequestDecision } from '../../../types/eqms.types';

export function formatRequestDate(value: string | null | undefined, language: string) {
  if (!value) return '';
  return new Intl.DateTimeFormat(language.startsWith('fr') ? 'fr-CA' : 'en-CA', {
    timeZone: 'America/Montreal', dateStyle: 'medium', timeStyle: 'short',
  }).format(new Date(value));
}

/** The revision records why it was opened; the request text is that reason. */
export function revisionReasonFor(requests: EqmsUpdateRequest[]) {
  const reason = requests.map((request) => `${request.requesterName}: ${request.reason}`).join('\n');
  return reason.slice(0, 2000);
}

export function useUpdateRequestActions() {
  const { t } = useTranslation('qms');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const decide = useMutation({
    mutationFn: ({ request, status, note }: { request: EqmsUpdateRequest; status: EqmsUpdateRequestDecision; note?: string }) =>
      decideUpdateRequest(request.id, { status, expectedStatus: request.status, ...(note?.trim() ? { note: note.trim() } : {}) }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: EQMS_QUERY_KEYS.root }),
  });

  // Native documents open (or resume) their revision draft right away; the
  // request then closes when that revision becomes Effective. Uploaded
  // sources have no revision draft, so the owner marks them done.
  const addressNow = useMutation({
    mutationFn: async (request: EqmsUpdateRequest) => {
      await decideUpdateRequest(request.id, { status: 'addressing', expectedStatus: request.status });
      if (request.document.sourceKind !== 'authored') return { draftId: null, frozen: false };
      try {
        const { draft } = await openControlledAuthoringDraft(request.documentId, revisionReasonFor([request]));
        return { draftId: draft.id, frozen: false };
      } catch (error) {
        const code = error instanceof ApiError ? (error.data as { code?: string } | undefined)?.code : undefined;
        if (code === 'AUTHORING_FROZEN') return { draftId: null, frozen: true };
        throw error;
      }
    },
    onSuccess: ({ draftId, frozen }) => {
      if (draftId) navigate(`/eqms/authoring/${draftId}`);
      else toast.success(t(frozen ? 'updateRequests.revisionFrozen' : 'updateRequests.addressingUpload'));
    },
    onError: (error) => toast.error(error.message),
    onSettled: () => queryClient.invalidateQueries({ queryKey: EQMS_QUERY_KEYS.root }),
  });

  return { decide, addressNow };
}
